import { useInfiniteQuery } from "@tanstack/react-query";
import { Link, type LinkProps } from "@tanstack/react-router";
import { AppIcon, Button, ListTable, Notice, SelectField, TextField, type AppIconName, type ListColumn } from "@wilco/ui";
import { useDeferredValue, useEffect, useState, type ReactNode } from "react";
import { useCan } from "../auth.ts";
import { t } from "../i18n.ts";
import { errorNotice } from "../stock/ShelfPage.tsx";
import "./docs.css";

/*
 * Общий экран списка документов (handoff 3 ноября): поиск, чипы статуса,
 * сортировка, загрузка страницами, пустое состояние, «Створити», строка
 * ведёт в карточку. На ПК таблица, на телефоне карточки (ListTable).
 * Поиск, статус и сортировка живут в адресе: список можно открыть ссылкой
 * и вернуться к нему кнопкой «назад» тем же.
 */

/** Параметры списка в адресе: ?q=&status=&sort=. */
export interface ListSearch {
  q?: string;
  status?: string;
  sort?: string;
}

/** validateSearch маршрута списка. */
export const listSearch = (s: Record<string, unknown>): ListSearch => {
  const str = (v: unknown) => (typeof v === "string" && v ? v : undefined);
  return { q: str(s.q), status: str(s.status), sort: str(s.sort) };
};

/** Запрос страницы: фильтры отдельными параметрами, cursor из прошлой страницы. */
export interface ListQuery {
  q: string;
  status: string | null;
  sort: string;
  /** Непрозрачный курсор сервера; null на первой странице. */
  cursor: string | null;
  limit: number;
}

/**
 * Страница как у сервера (wilco.web.api.CursorPage): строки и курсор
 * следующей, null на последней. Общего числа строк нет: «Показати ще» по
 * курсору, сервер получает его обратно в `?cursor=` как есть.
 */
export interface Page<Row> {
  items: Row[];
  next: string | null;
}

export interface Choice {
  value: string;
  label: string;
}

export interface DocumentListProps<Row> {
  title: string;
  icon?: AppIconName;
  /** Ключ Query списка; параметры поиска допишутся к нему. */
  queryKey: readonly unknown[];
  load: (query: ListQuery) => Promise<Page<Row>>;
  columns: ListColumn<Row>[];
  rowKey: (row: Row) => string;
  onOpen?: (row: Row) => void;
  /** Чипы статуса; «Усі» добавляется первым. */
  statuses?: Choice[];
  /** Сортировки; первая по умолчанию. */
  sorts: Choice[];
  search: ListSearch;
  onSearch: (next: ListSearch) => void;
  searchHint?: string;
  create?: { label: string; to: NonNullable<LinkProps["to"]>; permission?: string };
  empty: { title: string; body?: string };
  /** Над списком: задачи и подсказки модуля. */
  before?: ReactNode;
  pageSize?: number;
}

export function DocumentList<Row>(props: DocumentListProps<Row>) {
  const { search, onSearch, sorts, statuses = [], pageSize = 50 } = props;
  const can = useCan();
  // Поле поиска живое, в адрес уходит отложенное: каждая буква не делает
  // новую запись истории и новый запрос.
  const [text, setText] = useState(search.q ?? "");
  const q = useDeferredValue(text.trim());
  useEffect(() => {
    if ((search.q ?? "") !== q) onSearch({ ...search, q: q || undefined });
  }, [q, search, onSearch]);

  const sort = sorts.some((s) => s.value === search.sort) ? (search.sort ?? "") : (sorts[0]?.value ?? "");
  const status = statuses.some((s) => s.value === search.status) ? (search.status ?? null) : null;
  const list = useInfiniteQuery({
    queryKey: [...props.queryKey, { q: search.q ?? "", status, sort }],
    queryFn: ({ pageParam }) => props.load({ q: search.q ?? "", status, sort, cursor: pageParam, limit: pageSize }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered = Boolean(search.q) || status !== null;

  return (
    <div className="page doc-list">
      <div className="page-head">
        <h1 className="page-title page-title-icon">
          {props.icon ? <AppIcon name={props.icon} size={32} /> : null}
          {props.title}
        </h1>
        {props.create && (!props.create.permission || can(props.create.permission)) ? (
          <Button asChild variant="create">
            <Link to={props.create.to}>{props.create.label}</Link>
          </Button>
        ) : null}
      </div>
      {props.before}
      <div className="doc-list-bar">
        <form role="search" className="doc-list-search" onSubmit={(e) => e.preventDefault()}>
          <TextField label={t("docs.search")} hideLabel placeholder={props.searchHint ?? t("docs.search")} value={text} onChange={(e) => setText(e.target.value)} autoComplete="off" />
        </form>
        <SelectField label={t("docs.sort")} hideLabel value={sort} onChange={(e) => onSearch({ ...search, sort: e.target.value })} options={sorts} className="doc-list-sort" />
      </div>
      {statuses.length > 0 ? (
        <div className="doc-chips" role="group" aria-label={t("docs.status")}>
          {[{ value: "", label: t("docs.all") }, ...statuses].map((s) => {
            const on = (status ?? "") === s.value;
            return (
              <button key={s.value || "all"} type="button" className="doc-chip" aria-pressed={on} onClick={() => onSearch({ ...search, status: s.value || undefined })}>
                {s.label}
              </button>
            );
          })}
        </div>
      ) : null}
      {list.error ? <Notice tone="danger" title={errorNotice(list.error).title} className="stock-notice" /> : null}
      {list.data && rows.length === 0 ? (
        <div className="doc-empty">
          <p className="doc-empty-title">{filtered ? t("docs.notFound") : props.empty.title}</p>
          {filtered ? (
            <Button
              variant="ghost"
              onClick={() => {
                setText("");
                onSearch({ sort: search.sort });
              }}
            >
              {t("docs.reset")}
            </Button>
          ) : props.empty.body ? (
            <p className="w-muted">{props.empty.body}</p>
          ) : null}
        </div>
      ) : (
        <ListTable label={props.title} columns={props.columns} rows={rows} rowKey={props.rowKey} onRowClick={props.onOpen} />
      )}
      <div className="doc-list-foot">
        {list.hasNextPage ? (
          <Button variant="secondary" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
            {t("docs.more")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Список целиком с сервера, который не умеет страницы и фильтры: поиск,
 * статус, сортировка и страницы считаются в браузере, курсор это смещение.
 * Когда сервер отдаст CursorPage, модуль заменит load, экран тот же.
 */
export function clientPaged<Row>(
  all: () => Promise<Row[]>,
  opts: {
    text: (row: Row) => string;
    status?: (row: Row) => string;
    compare: Record<string, (a: Row, b: Row) => number>;
  },
): (query: ListQuery) => Promise<Page<Row>> {
  return async ({ q, status, sort, cursor, limit }) => {
    const offset = Number(cursor ?? 0);
    const needle = q.toLocaleLowerCase("uk");
    const rows = (await all())
      .filter((r) => !needle || opts.text(r).toLocaleLowerCase("uk").includes(needle))
      .filter((r) => status === null || !opts.status || opts.status(r) === status);
    const cmp = opts.compare[sort];
    if (cmp) rows.sort(cmp);
    const end = offset + limit;
    return { items: rows.slice(offset, end), next: end < rows.length ? String(end) : null };
  };
}
